// Version switcher + side-by-side diff (Module 01 §4, S3/S4). The switcher is a
// labelled version picker; the diff view is a generic side-by-side comparison of
// two version snapshots keyed by a stable line/step key, highlighting
// added / removed / changed rows. Screens adapt their BOM lines or routing steps
// into `DiffRow`s.

import { enumLabel } from "../../../../lib/enum-label";
import { useTranslation } from "react-i18next";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Select } from "../../../../components/select";
import { isScheduledVersion } from "./version-schedule";

export interface VersionOption {
  id: string;
  version: number;
  status: string;
  /** Optional: lets the option read "Scheduled" for a future-dated active
   * version when the caller also passes `today` (spec 01 §9.16, D-261). */
  effective_from?: string | null | undefined;
}

export function VersionSwitcher({
  versions,
  value,
  onChange,
  label,
  today,
}: {
  versions: readonly VersionOption[];
  value: string;
  onChange: (id: string) => void;
  label?: string | undefined;
  /** The business date "scheduled" is judged against (facility-local, D-089). */
  today?: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <label className="version-switcher cat-fld">
      <span className="version-switcher-label">{label ?? t("catalogue.version.label")}</span>
      <Select
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        {versions.map((v) => {
          const scheduled =
            today !== undefined && isScheduledVersion(v.status, v.effective_from, today);
          const statusLabel = scheduled
            ? t("catalogue.status.scheduled")
            : enumLabel(t, "catalogue.status", v.status);
          return (
            <option value={v.id} key={v.id}>
              v{v.version} · {statusLabel}
            </option>
          );
        })}
      </Select>
    </label>
  );
}

export interface DiffRow {
  /** Stable identity across versions (e.g. BOM line_no, routing step_no). */
  key: string;
  /** Human label for the row. */
  label: string;
  /** Comparable field snapshot; equality is by JSON value. */
  fields: Record<string, unknown>;
}

type RowState = "added" | "removed" | "changed" | "unchanged";

function diffState(before: DiffRow | undefined, after: DiffRow | undefined): RowState {
  if (before === undefined) return "added";
  if (after === undefined) return "removed";
  return JSON.stringify(before.fields) === JSON.stringify(after.fields) ? "unchanged" : "changed";
}

export function DiffView({
  before,
  after,
}: {
  before: readonly DiffRow[];
  after: readonly DiffRow[];
}) {
  const { t } = useTranslation();
  const beforeByKey = new Map(before.map((r) => [r.key, r]));
  const afterByKey = new Map(after.map((r) => [r.key, r]));
  const keys = [...new Set([...beforeByKey.keys(), ...afterByKey.keys()])];

  interface Pair {
    key: string;
    before: DiffRow | undefined;
    after: DiffRow | undefined;
    state: RowState;
  }
  const pairs: Pair[] = keys.map((key) => {
    const b = beforeByKey.get(key);
    const a = afterByKey.get(key);
    return { key, before: b, after: a, state: diffState(b, a) };
  });

  const columns: DataTableColumn<Pair>[] = [
    {
      key: "before",
      header: t("catalogue.diff.before"),
      render: (p) => (p.before !== undefined ? <DiffCell row={p.before} /> : null),
    },
    {
      key: "after",
      header: t("catalogue.diff.after"),
      render: (p) => (p.after !== undefined ? <DiffCell row={p.after} /> : null),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={pairs}
      rowKey={(p) => p.key}
      rowTestId="diff-row"
      rowClassName={(p) => `diff-row diff-${p.state}`}
      rowProps={(p) => ({ "data-diff": p.state })}
      caption={t("catalogue.version.compare")}
      empty={<p className="diff-empty">{t("catalogue.diff.noChanges")}</p>}
    />
  );
}

function formatFieldValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value === null) return "null";
  if (value === undefined) return "";
  return JSON.stringify(value);
}

function DiffCell({ row }: { row: DiffRow }) {
  return (
    <div className="diff-cell">
      <span className="diff-cell-label">{row.label}</span>
      <dl className="diff-cell-fields">
        {Object.entries(row.fields).map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{formatFieldValue(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
